import { 
  doc,
  setDoc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  Timestamp,
  updateDoc,
  arrayUnion,
  arrayRemove,
  deleteDoc
} from 'firebase/firestore';
import { db } from '@/services/firebase';

interface ReadReceipt {
  userId: string;
  readAt: Timestamp;
}

interface MessageReadReceipts {
  messageId: string;
  chatId: string;
  readBy: ReadReceipt[];
  createdAt: Timestamp;
}

export class ReadReceiptsService {
  private static instance: ReadReceiptsService;

  public static getInstance(): ReadReceiptsService {
    if (!ReadReceiptsService.instance) {
      ReadReceiptsService.instance = new ReadReceiptsService();
    }
    return ReadReceiptsService.instance;
  }

  // Mark a message as read by a specific user
  async markMessageAsRead(chatId: string, messageId: string, userEmail: string): Promise<void> {
    try {
      const receiptRef = doc(db, 'messageReadReceipts', `${chatId}_${messageId}`);
      
      // Check if document exists
      const receiptDoc = await getDoc(receiptRef);
      
      const readReceipt: ReadReceipt = {
        userId: userEmail,
        readAt: Timestamp.now()
      };

      if (receiptDoc.exists()) {
        // Document exists, check if user already read it
        const data = receiptDoc.data() as MessageReadReceipts;
        const alreadyRead = data.readBy?.some(receipt => receipt.userId === userEmail);
        
        if (!alreadyRead) {
          // Add this user to the readBy array
          await updateDoc(receiptRef, {
            readBy: arrayUnion(readReceipt)
          });
          console.log(`✓ Message ${messageId} marked as read by ${userEmail}`);
        }
      } else {
        // Create new document
        const newReceipt: MessageReadReceipts = {
          messageId,
          chatId,
          readBy: [readReceipt],
          createdAt: Timestamp.now()
        };
        
        await setDoc(receiptRef, newReceipt);
        console.log(`✓ Created read receipt for message ${messageId}, read by ${userEmail}`);
      }
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  }

  // Mark multiple messages as read (bulk operation)
  async markMessagesAsRead(chatId: string, messageIds: string[], userEmail: string): Promise<void> {
    try {
      const promises = messageIds.map(messageId => 
        this.markMessageAsRead(chatId, messageId, userEmail)
      );
      
      await Promise.all(promises);
      console.log(`✓ Bulk marked ${messageIds.length} messages as read by ${userEmail}`);
    } catch (error) {
      console.error('Error bulk marking messages as read:', error);
    }
  }

  // Check if a message has been read by a specific user
  async isMessageReadByUser(chatId: string, messageId: string, userEmail: string): Promise<boolean> {
    try {
      const receiptRef = doc(db, 'messageReadReceipts', `${chatId}_${messageId}`);
      const receiptDoc = await getDoc(receiptRef);
      
      if (!receiptDoc.exists()) {
        return false;
      }
      
      const data = receiptDoc.data() as MessageReadReceipts;
      return data.readBy?.some(receipt => receipt.userId === userEmail) || false;
    } catch (error) {
      console.error('Error checking if message is read:', error);
      return false;
    }
  }

  // Get read receipts for a specific message
  async getMessageReadReceipts(chatId: string, messageId: string): Promise<ReadReceipt[]> {
    try {
      const receiptRef = doc(db, 'messageReadReceipts', `${chatId}_${messageId}`);
      const receiptDoc = await getDoc(receiptRef);
      
      if (!receiptDoc.exists()) {
        return [];
      }
      
      const data = receiptDoc.data() as MessageReadReceipts;
      return data.readBy || [];
    } catch (error) {
      console.error('Error getting message read receipts:', error);
      return [];
    }
  }

  // Get all unread messages for a user in a specific chat
  async getUnreadMessagesInChat(chatId: string, userEmail: string, messageIds: string[]): Promise<string[]> {
    try {
      const unreadMessages: string[] = [];
      
      for (const messageId of messageIds) {
        const isRead = await this.isMessageReadByUser(chatId, messageId, userEmail);
        if (!isRead) {
          unreadMessages.push(messageId);
        }
      }
      
      return unreadMessages;
    } catch (error) {
      console.error('Error getting unread messages:', error);
      return [];
    }
  }

  // Get count of unread messages for a user in a specific chat
  async getUnreadCountInChat(chatId: string, userEmail: string, messageIds: string[]): Promise<number> {
    try {
      const unreadMessages = await this.getUnreadMessagesInChat(chatId, userEmail, messageIds);
      return unreadMessages.length;
    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  }

  // Clean up read receipts for deleted messages
  async cleanupReadReceipts(chatId: string, messageId: string): Promise<void> {
    try {
      const receiptRef = doc(db, 'messageReadReceipts', `${chatId}_${messageId}`);
      await deleteDoc(receiptRef);
      console.log(`✓ Cleaned up read receipts for deleted message ${messageId}`);
    } catch (error) {
      console.error('Error cleaning up read receipts:', error);
    }
  }
}

export const readReceiptsService = ReadReceiptsService.getInstance();
